import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, errorMessage } from '../lib/api';
import { TriDot } from '../ui/TriDot';
import { LivePulse } from '../ui/LivePulse';
import { Pill } from '../ui/Pill';
import { Icon } from '../ui/Icons';
import type { Conversation, ConversationDetail } from '../lib/types';

type Filter = 'all' | 'active' | 'lead' | 'unanswered';

const isActive = (c: Pick<Conversation, 'endedAt' | 'startedAt'>) =>
  !c.endedAt && Date.now() - new Date(c.startedAt).getTime() < 10 * 60 * 1000;

export const Conversations = () => {
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  const listQ = useQuery<{ conversations: Conversation[] }>({
    queryKey: ['conversations'],
    queryFn: async () => (await api.get('/api/tenant/conversations?limit=50')).data,
  });

  const all = listQ.data?.conversations ?? [];
  const today = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return all.filter((c) => new Date(c.startedAt) >= start).length;
  }, [all]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return all.filter((c) => {
      if (filter === 'active' && !isActive(c)) return false;
      if (filter === 'lead' && !c.lead) return false;
      if (filter === 'unanswered' && c.messageCount > 1) return false;
      if (!term) return true;
      return (
        c.visitorId.toLowerCase().includes(term) ||
        (c.lead?.email?.toLowerCase().includes(term) ?? false) ||
        (c.lead?.phone?.toLowerCase().includes(term) ?? false)
      );
    });
  }, [all, filter, search]);

  return (
    <div className="flex-1 flex min-h-0 h-[calc(100vh-48px)]">
      {/* List */}
      <aside className="w-[440px] shrink-0 border-r border-line flex flex-col min-h-0 bg-white">
        <div className="px-6 pt-5 pb-3 shrink-0">
          <div className="flex items-baseline justify-between gap-3">
            <h1 className="m-0 text-[19px] font-semibold text-ink tracking-tight">Conversations</h1>
            <span className="text-[11.5px] text-ink-subtle">
              {all.length} total · {today} today
            </span>
          </div>
          <label className="flex items-center gap-1.5 px-2.5 py-1.5 border border-line rounded-[9px] mt-3 text-[12.5px]">
            <Icon.Search size={14} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search messages, visitor ID…"
              className="flex-1 bg-transparent border-0 outline-none text-ink placeholder:text-ink-subtle"
            />
          </label>
          <div className="flex gap-1 mt-2.5 text-[11px]">
            {(['all', 'active', 'lead', 'unanswered'] as Filter[]).map((f) => {
              const labels: Record<Filter, string> = {
                all: 'All',
                active: 'Active now',
                lead: 'Lead captured',
                unanswered: 'Unanswered',
              };
              const on = filter === f;
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`px-2.5 py-1 rounded-full transition-colors ${
                    on
                      ? 'bg-ink text-white'
                      : 'bg-transparent text-ink-muted border border-line-strong hover:text-ink'
                  }`}
                >
                  {labels[f]}
                </button>
              );
            })}
          </div>
        </div>

        <ul className="list-none m-0 p-0 overflow-y-auto flex-1">
          {listQ.isLoading ? (
            <li className="p-6 text-[12.5px] text-ink-subtle text-center">Loading…</li>
          ) : listQ.error ? (
            <li className="p-6 text-[12.5px] text-rose-700 text-center">{errorMessage(listQ.error)}</li>
          ) : filtered.length === 0 ? (
            <li className="p-6 text-[12.5px] text-ink-subtle text-center">
              {all.length === 0 ? 'No conversations yet.' : 'No conversations match these filters.'}
            </li>
          ) : (
            filtered.map((c) => {
              const active = isActive(c);
              const on = selected === c.id;
              return (
                <li
                  key={c.id}
                  onClick={() => setSelected(c.id)}
                  className={`relative px-6 py-3 border-t border-line cursor-pointer transition-colors ${
                    on ? 'bg-[#F8F8FB]' : 'hover:bg-bg'
                  }`}
                >
                  {on && (
                    <span className="absolute left-0 top-2 bottom-2 w-[3px] bg-accent rounded-full" />
                  )}
                  <div className="flex items-start gap-3">
                    <div
                      className={`relative w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 ${
                        active ? 'bg-emerald-50 text-emerald-700' : 'bg-[#F2F2F5] text-ink-soft'
                      }`}
                    >
                      {c.visitorId.slice(0, 2).toUpperCase()}
                      {active && (
                        <span className="absolute -right-0.5 -bottom-0.5 w-2.5 h-2.5 rounded-full bg-emerald-500 border-2 border-white" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-[12.5px] font-medium text-ink font-mono truncate">
                          v-{c.visitorId.slice(0, 12)}
                        </span>
                        <span className="text-[11px] text-ink-subtle tabular-nums">
                          {formatTime(c.startedAt)}
                        </span>
                      </div>
                      <div className="text-[11.5px] text-ink-muted mt-0.5 flex gap-2 items-center">
                        <span>{formatDate(c.startedAt)}</span>
                        <span>·</span>
                        <span>
                          {c.messageCount} {c.messageCount === 1 ? 'msg' : 'msgs'}
                        </span>
                      </div>
                      {(active || c.lead) && (
                        <div className="mt-1.5 flex gap-1.5 items-center flex-wrap">
                          {active && <Pill tone="live" dot>live</Pill>}
                          {c.lead && (
                            <Pill tone="won" dot>
                              lead{' '}
                              {(c.lead.email ?? c.lead.phone) &&
                                `· ${truncate(c.lead.email ?? c.lead.phone ?? '', 18)}`}
                            </Pill>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })
          )}
        </ul>
      </aside>

      {/* Transcript */}
      <section className="flex-1 min-w-0 flex flex-col min-h-0 bg-[#FAFAFB]">
        {selected ? (
          <Transcript id={selected} onClose={() => setSelected(null)} />
        ) : (
          <EmptyTranscript />
        )}
      </section>
    </div>
  );
};

// ---- Transcript ----

const Transcript = ({ id, onClose }: { id: string; onClose: () => void }) => {
  const q = useQuery<{ conversation: ConversationDetail }>({
    queryKey: ['conversation', id],
    queryFn: async () => (await api.get(`/api/tenant/conversations/${id}`)).data,
  });

  const c = q.data?.conversation;
  const active = c ? isActive(c) : false;

  return (
    <>
      <header className="bg-white border-b border-line px-7 py-3.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-gradient-tri text-white flex items-center justify-center shrink-0">
            <TriDot size={3.5} gap={2.5} tone="accent" />
          </div>
          <div className="min-w-0">
            <div className="text-[13.5px] font-semibold text-ink flex items-center gap-2">
              <span className="font-mono truncate">v-{(c?.visitorId ?? id).slice(0, 12)}</span>
              {c?.lead && <Pill tone="won" dot>lead captured</Pill>}
              {active && <LivePulse label="Live" />}
            </div>
            <div className="text-[11.5px] text-ink-subtle mt-0.5 truncate">
              {c
                ? `${new Date(c.startedAt).toLocaleString()} · ${c.messageCount} ${c.messageCount === 1 ? 'message' : 'messages'}`
                : 'Loading…'}
            </div>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            className="btn-ghost"
            onClick={() => {
              if (c) {
                navigator.clipboard.writeText(`${window.location.origin}/conversations/${c.id}`);
              }
            }}
          >
            <Icon.Copy size={13} />
            Copy link
          </button>
          <button type="button" className="btn-ghost" onClick={onClose}>
            <Icon.X size={13} />
            Close
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-7 py-6 flex flex-col gap-3.5">
        {q.isLoading && (
          <div className="text-[12.5px] text-ink-subtle text-center py-10">Loading transcript…</div>
        )}
        {q.error && (
          <div className="text-[12.5px] text-rose-700 text-center py-10">
            {errorMessage(q.error)}
          </div>
        )}
        {c && (
          <>
            <Stamp>{new Date(c.startedAt).toLocaleString(undefined, { weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</Stamp>
            {c.messages.map((m) => {
              if (m.role === 'TOOL') return <ToolCallRow key={m.id} message={m} />;
              if (m.role === 'SYSTEM') return null;
              const side = m.role === 'USER' ? 'user' : 'bot';
              return (
                <Msg
                  key={m.id}
                  side={side}
                  content={m.content}
                  agentName={c.agent.name}
                  at={m.createdAt}
                  latencyMs={m.latencyMs}
                />
              );
            })}
            {active && <Msg side="bot" typing agentName={c.agent.name} />}
          </>
        )}
      </div>

      <div className="px-7 py-3 border-t border-line bg-white">
        <div
          className="flex items-center gap-2.5 px-3 py-2 rounded-[11px] border bg-bg"
          style={{ borderColor: '#E2E2E6' }}
          title="Sending a note to visitors is coming soon"
        >
          <Icon.Sparkle size={15} strokeWidth={1.4} />
          <span className="text-[12.5px] text-ink-subtle flex-1">
            Sending a note to a visitor will land here in a future release.
          </span>
          <button
            type="button"
            disabled
            className="w-7 h-7 rounded-md bg-ink text-white flex items-center justify-center opacity-40 cursor-not-allowed"
            aria-label="Send (coming soon)"
          >
            <Icon.ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </>
  );
};

const EmptyTranscript = () => (
  <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-8">
    <div className="w-14 h-14 rounded-2xl bg-gradient-tri text-white flex items-center justify-center">
      <TriDot size={6} gap={4} tone="accent" />
    </div>
    <div className="text-[15px] text-ink font-medium">Select a conversation</div>
    <div className="text-[12.5px] text-ink-subtle max-w-sm">
      Pick a chat on the left to see the full transcript — including any tool calls your agent made and which knowledge sources it cited.
    </div>
  </div>
);

// ---- bits ----

const Stamp = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-2.5 text-[11px] text-ink-subtle my-1">
    <span className="flex-1 h-px bg-line" />
    <span>{children}</span>
    <span className="flex-1 h-px bg-line" />
  </div>
);

interface MsgProps {
  side: 'user' | 'bot';
  content?: string;
  typing?: boolean;
  agentName?: string;
  at?: string;
  latencyMs?: number | null;
}

const Msg = ({ side, content, typing, agentName, at, latencyMs }: MsgProps) => {
  if (side === 'user') {
    return (
      <div className="flex justify-end gap-2.5">
        <div className="max-w-[72%]">
          <div className="bg-ink text-white text-[13px] leading-relaxed py-2.5 px-3.5 rounded-[14px_14px_4px_14px] whitespace-pre-wrap">
            {content || <span className="text-white/60 italic">(empty)</span>}
          </div>
          <div className="text-[10.5px] text-ink-subtle mt-1 text-right tabular-nums">
            visitor{at ? ` · ${formatTime(at)}` : ''}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-7 h-7 rounded-full bg-gradient-tri flex items-center justify-center shrink-0">
        <TriDot size={3.5} gap={2.5} tone="accent" />
      </div>
      <div className="max-w-[72%]">
        <div className="bg-white text-ink text-[13px] leading-relaxed py-2.5 px-3.5 rounded-[4px_14px_14px_14px] border border-line whitespace-pre-wrap shadow-[0_1px_0_rgba(15,15,40,.02)]">
          {typing ? <TypingDots /> : content || <span className="text-ink-subtle italic">(empty)</span>}
        </div>
        <div className="text-[10.5px] text-ink-subtle mt-1">
          {agentName ?? 'Agent'}
          {at && ` · ${formatTime(at)}`}
          {!typing && latencyMs != null && ` · ${latencyMs}ms`}
        </div>
      </div>
    </div>
  );
};

const TypingDots = () => (
  <span className="inline-flex items-center gap-1 py-1">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="w-1.5 h-1.5 rounded-full bg-ink-subtle animate-typing-bounce"
        style={{ animationDelay: `${i * 0.15}s` }}
      />
    ))}
  </span>
);

interface ToolUseShape {
  name?: string;
  input?: Record<string, unknown>;
  output?: unknown;
  ok?: boolean;
  is_error?: boolean;
}

const ToolCallRow = ({
  message,
}: {
  message: ConversationDetail['messages'][number];
}) => {
  const tu = (message.toolUse ?? {}) as ToolUseShape;
  const fn = tu.name ?? 'tool_call';
  const args = tu.input ?? {};
  const ok = tu.ok ?? !tu.is_error;
  const argStr = Object.entries(args)
    .map(([k, v], i) => `${i ? ', ' : ''}${k}: ${typeof v === 'string' ? `"${v}"` : JSON.stringify(v)}`)
    .join('');
  return (
    <div className="flex justify-center">
      <div className="inline-flex items-center gap-2.5 py-1.5 pl-2 pr-3 rounded-full bg-white border border-dashed border-line-strong text-[11.5px] text-ink-muted font-mono">
        <span
          className={`w-[18px] h-[18px] rounded-full flex items-center justify-center ${
            ok ? 'bg-emerald-50 text-emerald-700' : 'bg-accent-soft text-accent-ink'
          }`}
        >
          {ok ? <Icon.Check size={11} /> : <Icon.Sparkle size={11} />}
        </span>
        <span className="text-ink-soft font-medium">{fn}</span>
        <span className="text-ink-subtle">(</span>
        <span className="text-accent-ink max-w-[360px] truncate">{argStr || '…'}</span>
        <span className="text-ink-subtle">)</span>
      </div>
    </div>
  );
};

// ---- helpers ----

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const truncate = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
